'use client'

import dynamic from 'next/dynamic'
import s from './PlugAds.module.sass'

import { FC, memo, useEffect, useId, useRef, useState } from 'react'

import { useAppDispatch, useAppSelector, useInjectWidgetReducer } from '@app/model/store/client'

import PlugAdsReducer, { addIdPlugElement, removeIdPlugElement } from '@widgets/Common/PlugAds/model/slice'
import { TypePlug } from '@widgets/Common/PlugAds/types/types'
import { TypeMappingSelectionOutput } from '@shared/api/middleware/mappingAPI/selection/type'

import { BlockBoundary } from '@shared/lib/components/BlockBoundary'
import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'
import { Text } from '@shared/ui/Typography/Text'

const LazyPlugAdsSwiperBody = dynamic(() => import('./PlugAdsSwiperBody').then((m) => m.default), {
  ssr: false,
  loading: () => null,
})

type PlugAdsSwiperMountProps = {
  plugIdValid: string
  plugType: TypePlug | null
  list: TypeMappingSelectionOutput[] | undefined
  isMobile: boolean
}

const PlugAdsSwiperMount: FC<PlugAdsSwiperMountProps> = ({ plugIdValid, plugType, list, isMobile }) => {
  const [swiperModuleReady, setSwiperModuleReady] = useState(false)

  return (
    <>
      <LazyPlugAdsSwiperBody
        plugIdValid={plugIdValid}
        plugType={plugType}
        list={list}
        isMobile={isMobile}
        onModuleMount={() => setSwiperModuleReady(true)}
      />
      {!swiperModuleReady && <div style={{ minHeight: 120 }} aria-busy='true' />}
    </>
  )
}

type TypeProps = {
  className?: string
}

const PlugAds: FC<TypeProps> = ({ className = '' }) => {
  useInjectWidgetReducer('plugAds', PlugAdsReducer)

  const plugId = useId()
  const plugIdValid = plugId.replaceAll(':', '')
  const plugsState = useAppSelector((store) => store.widgets.plugAds)
  const dispatch = useAppDispatch()
  const [plugType, setPlugType] = useState<TypePlug>(null)
  const { isMobile, designEnv } = useClientResponsive()
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (plugsState.status === 'success' && plugType) dispatch(addIdPlugElement({ id: plugIdValid, type: plugType }))
    return () => {
      dispatch(removeIdPlugElement({ id: plugIdValid }))
    }
  }, [plugsState.status, plugType])

  useEffect(() => {
    if (isMobile) {
      setPlugType('mobile-slider')
      return
    }
    if (!wrapperRef.current) return
    const parentWidth = wrapperRef.current.parentElement?.clientWidth ?? 0
    setPlugType(parentWidth > 500 ? 'desktop-large' : 'desktop-shrink')
  }, [wrapperRef.current, designEnv])

  const isShowComponent = Boolean(
    !plugType ||
    plugsState.status == 'loading' ||
    (plugsState.status === 'success' && plugsState.plugs[plugIdValid]?.list.length),
  )

  const plugList = plugsState.plugs[plugIdValid]?.list

  return (
    isShowComponent && (
      <BlockBoundary>
        <div className={`${s.wrapper} ${className}`} ref={wrapperRef} data-plug-type={plugType}>
          <Text sizes={'S'} className={s.title}>
            Вместо рекламы
          </Text>
          <PlugAdsSwiperMount
            key={plugType ?? 'pending'}
            plugIdValid={plugIdValid}
            plugType={plugType}
            list={plugList}
            isMobile={isMobile}
          />
        </div>
      </BlockBoundary>
    )
  )
}

export default memo(PlugAds)
